import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { Box, Tooltip, IconButton } from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import AssessmentIcon from '@mui/icons-material/Assessment';

import { SCUIContent } from '../../atomic/organisms';
import { SCUIBreadCrumb, SCUICustomDataGrid } from '../../atomic/molecules';
import { SCUILoadingLinear } from '../../atomic/atoms';

import { PageUrl } from '../../types/enums';
import { PostCheckoutCommand } from '../../services/command';
import TimeHelper from '../../utils/time';

export function EtbisReports() {
    const [pageSize, setPageSize] = useState<number>(10);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [gridData, setGridData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const { t } = useTranslation();

    const fetchReports = () => {
        setIsLoading(true);
        PostCheckoutCommand.ListEtbisReports()
            .then((res: any) => {
                if (res && res.data) {
                    const gridDataWithId = (res.data || []).map((item: any) => ({
                        ...item,
                        uniqueId: crypto.randomUUID(),
                    }));
                    setGridData(gridDataWithId);
                } else if (res && Array.isArray(res)) {
                    const gridDataWithId = (res || []).map((item: any) => ({
                        ...item,
                        uniqueId: crypto.randomUUID(),
                    }));
                    setGridData(gridDataWithId);
                } else {
                    setGridData([]);
                }
            })
            .catch(() => {
                Swal.fire({
                    title: t('general.error'),
                    text: t('general.anErrorOccured'),
                    icon: 'error',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchReports();
    }, []);

    const formatSize = (bytes: number) => {
        if (bytes === 0) return '0 KB';
        const k = 1024;
        const dm = 1;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    };

    const handleDownload = (fileName: string) => {
        setIsLoading(true);
        PostCheckoutCommand.DownloadEtbisReport({ fileName })
            .then((res: Blob) => {
                const url = window.URL.createObjectURL(new Blob([res]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName || 'report.zip');
                document.body.appendChild(link);
                link.click();
                link.parentNode?.removeChild(link);
                window.URL.revokeObjectURL(url);
            })
            .catch(() => {
                Swal.fire({
                    title: t('general.error'),
                    text: t('general.anErrorOccured'),
                    icon: 'error',
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleDelete = (fileName: string) => {
        Swal.fire({
            title: `<span style="font-size: 20px; font-weight: 600; color: #1a1a1a;">${t('etbisReports.messages.deleteConfirmTitle')}</span>`,
            html: t('etbisReports.messages.deleteConfirmText'),
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#fff',
            confirmButtonText: t('etbisReports.messages.yes'),
            cancelButtonText: t('etbisReports.messages.cancel'),
            customClass: {
                confirmButton: 'swal2-confirm swal2-styled',
                cancelButton: 'swal2-cancel swal2-styled',
                actions: 'swal2-actions',
            },
            buttonsStyling: false,
            didOpen: () => {
                const confirmBtn = Swal.getConfirmButton();
                const cancelBtn = Swal.getCancelButton();

                if (confirmBtn) {
                    confirmBtn.style.backgroundColor = '#d32f2f';
                    confirmBtn.style.color = 'white';
                    confirmBtn.style.border = 'none';
                    confirmBtn.style.borderRadius = '4px';
                    confirmBtn.style.padding = '8px 24px';
                    confirmBtn.style.fontWeight = '500';
                    confirmBtn.style.fontSize = '14px';
                }

                if (cancelBtn) {
                    cancelBtn.style.backgroundColor = 'white';
                    cancelBtn.style.color = '#333';
                    cancelBtn.style.border = '1px solid #ccc';
                    cancelBtn.style.borderRadius = '4px';
                    cancelBtn.style.padding = '8px 24px';
                    cancelBtn.style.fontWeight = '500';
                    cancelBtn.style.fontSize = '14px';
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                setIsLoading(true);
                PostCheckoutCommand.DeleteEtbisReport({ fileName })
                    .then(() => {
                        Swal.fire({
                            icon: 'success',
                            title: t('etbisReports.messages.deleteSuccess'),
                            showConfirmButton: false,
                            timer: 1500,
                        });
                        // fetchReports zaten kendi içinde setIsLoading(true) ve (false) barındırıyor.
                        fetchReports();
                    })
                    .catch(() => {
                        Swal.fire({
                            title: t('general.error'),
                            text: t('etbisReports.messages.deleteError'),
                            icon: 'error',
                        });
                        setIsLoading(false);
                    });
            }
        });
    };

    const columnData = [
        {
            field: 'fileName',
            headerName: t('etbisReports.columns.fileName'),
            flex: 1,
            align: 'left',
            headerAlign: 'left',
        },
        {
            field: 'lastModified',
            headerName: t('etbisReports.columns.reportPeriod'),
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return TimeHelper.formatDateHour(params?.value || '-');
            },
        },
        {
            field: 'sizeBytes',
            headerName: t('etbisReports.columns.sizeBytes'),
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return formatSize(params?.value || 0);
            },
        },
        {
            field: 'actions',
            headerName: t('etbisReports.columns.actions'),
            width: 250,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                return (
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%', flexWrap: 'wrap' }}>
                        <Tooltip title={t('etbisReports.buttons.download')}>
                            <IconButton
                                color="primary"
                                onClick={() => handleDownload(params.row.fileName)}
                                sx={{
                                    backgroundColor: '#2e3b84',
                                    color: 'white',
                                    borderRadius: '4px',
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                    '&:hover': { backgroundColor: '#212a5e' },
                                }}
                            >
                                <FileDownloadIcon
                                    sx={{ fontSize: { xs: '18px', sm: '20px' }, mr: { xs: 0, sm: 0.5 } }}
                                />
                                <span
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        fontFamily: 'inherit',
                                        display: 'inline-block',
                                    }}
                                >
                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                        {t('etbisReports.buttons.download')}
                                    </Box>
                                </span>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('etbisReports.buttons.delete')}>
                            <IconButton
                                color="error"
                                onClick={() => handleDelete(params.row.fileName)}
                                sx={{
                                    backgroundColor: '#d32f2f',
                                    color: 'white',
                                    borderRadius: '4px',
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                    '&:hover': { backgroundColor: '#b71c1c' },
                                }}
                            >
                                <DeleteIcon sx={{ fontSize: { xs: '18px', sm: '20px' }, mr: { xs: 0, sm: 0.5 } }} />
                                <span
                                    style={{
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        fontFamily: 'inherit',
                                        display: 'inline-block',
                                    }}
                                >
                                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                        {t('etbisReports.buttons.delete')}
                                    </Box>
                                </span>
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <SCUIContent>
            <Box>
                <div style={{ marginBottom: '20px' }}>
                    <SCUIBreadCrumb
                        title={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AssessmentIcon color="primary" />
                                {t('etbisReports.title')}
                            </Box>
                        }
                        componentLink={PageUrl.etbisReports.url}
                    />
                </div>
            </Box>
            <Box sx={{ position: 'relative', minHeight: '100%' }}>
                {/* Loading bar'ı SCUIContent'in en üstüne, diğer componentlerden bağımsız yerleştiriyoruz */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: -24,
                        left: -24,
                        right: -24,
                        height: '4px',
                        zIndex: 9999,
                    }}
                >
                    <SCUILoadingLinear display={isLoading} />
                </Box>
                <Box>
                    <SCUICustomDataGrid
                        onPageSizeChange={(i: number) => {
                            setPageSize(i);
                        }}
                        pageSize={pageSize}
                        rowHeight={60}
                        onPageChange={(i: number) => {
                            setPageNumber(i);
                        }}
                        page={pageNumber}
                        getRowId={(row: any) => row.fileName || row.uniqueId}
                        rows={gridData || []}
                        columns={columnData}
                        paginationModeChange={false}
                        disableSelectionOnClick
                    />
                </Box>
            </Box>
        </SCUIContent>
    );
}
